import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Users, 
  Plus, 
  Trash2, 
  Edit,
  Search,
  Upload,
  Download,
  Phone,
  Mail
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/toast";
import { 
  getContacts, 
  createContact, 
  deleteContact,
  exportContactsJSON,
  exportContactsCSV
} from "@/lib/marketing-api";

const getPlatforms = (isRTL) => [
  { value: "telegram", label: isRTL ? "تلجرام" : "Telegram" },
  { value: "whatsapp", label: isRTL ? "واتساب" : "WhatsApp" },
  { value: "facebook", label: isRTL ? "فيسبوك" : "Facebook" },
  { value: "instagram", label: isRTL ? "انستجرام" : "Instagram" },
  { value: "tiktok", label: isRTL ? "تيك توك" : "TikTok" },
  { value: "youtube", label: isRTL ? "يوتيوب" : "YouTube" },
  { value: "linkedin", label: isRTL ? "لينكد إن" : "LinkedIn" },
  { value: "google_maps", label: isRTL ? "جوجل ماب" : "Google Maps" },
];

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const platforms = getPlatforms(isRTL);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    telegram_id: "",
    platform: "telegram",
    tags: [],
    source_group: ""
  });

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await getContacts();
      setContacts(response.data?.data || response.data || []);
    } catch (error) {
      toast.error(t('errors.serverError'));
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createContact(formData);
      toast.success(t('success.created'));
      setIsDialogOpen(false);
      setFormData({
        name: "",
        phone: "",
        email: "",
        telegram_id: "",
        platform: "telegram",
        tags: [],
        source_group: ""
      });
      loadContacts();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('marketingFull.confirmDeleteContact'))) return;
    try {
      await deleteContact(id);
      toast.success(t('success.deleted'));
      loadContacts();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const handleExport = async (format = 'json') => {
    try {
      if (format === 'csv') {
        const response = await exportContactsCSV();
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "contacts.csv";
        link.click();
        URL.revokeObjectURL(url);
      } else {
        const response = await exportContactsJSON();
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: "application/json" });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "contacts.json";
        link.click();
        URL.revokeObjectURL(url);
      }
      toast.success(t('success.exported'));
    } catch (error) {
      console.error("Export error:", error);
      toast.error(t('marketingFull.exportError'));
    }
  };

  const filteredContacts = Array.isArray(contacts) ? contacts.filter(contact => {
    const matchesSearch = (contact.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.phone && contact.phone.includes(searchTerm)) ||
      (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesPlatform = platformFilter === "all" || contact.platform === platformFilter;
    return matchesSearch && matchesPlatform;
  }) : [];

  const getPlatformLabel = (value) => {
    const platform = platforms.find(p => p.value === value);
    return platform ? platform.label : value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('marketingFull.contactsTitle')}</h1>
          <p className="text-muted-foreground mt-1">{t('marketingFull.contactsSubtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Select onValueChange={(value) => handleExport(value)}>
            <SelectTrigger className="w-[140px] border-2 border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20" data-testid="export-contacts-btn">
              <Download className="w-4 h-4 me-2 text-blue-400" />
              <span className="text-blue-400 font-medium">{t('common.export')}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="json">JSON</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-6 py-2 shadow-lg shadow-emerald-500/30 text-base" size="lg" data-testid="add-contact-btn">
                <Plus className="w-5 h-5 me-2" />
                + {t('marketingFull.addContact')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]" dir={isRTL ? "rtl" : "ltr"}>
              <DialogHeader>
                <DialogTitle>{t('marketingFull.addContact')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="form-group">
                  <Label htmlFor="name">{t('common.name')}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={isRTL ? "أدخل الاسم" : "Enter name"}
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label htmlFor="phone">{t('common.phone')}</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+966..."
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="email">{t('marketingFull.contactEmail')}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="example@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label htmlFor="platform">{t('marketingFull.platform')}</Label>
                    <Select 
                      value={formData.platform} 
                      onValueChange={(value) => setFormData({ ...formData, platform: value })}
                    >
                      <SelectTrigger data-testid="contact-platform-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {platforms.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-group">
                    <Label htmlFor="telegram_id">{t('marketingFull.telegramId')}</Label>
                    <Input
                      id="telegram_id"
                      value={formData.telegram_id}
                      onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value })}
                      placeholder="@username"
                      data-testid="contact-telegram-input"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <Label htmlFor="source_group">{t('marketingFull.sourceGroup')}</Label>
                  <Input
                    id="source_group"
                    value={formData.source_group}
                    onChange={(e) => setFormData({ ...formData, source_group: e.target.value })}
                    placeholder={t('marketingFull.sourceGroupPlaceholder')}
                    data-testid="contact-source-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" data-testid="submit-contact-btn">
                    {t('common.add')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('marketingFull.searchByNamePhoneEmail')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pe-10"
                data-testid="search-contacts-input"
              />
            </div>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="platform-filter">
                <SelectValue placeholder={t('marketingFull.platform')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('marketingFull.allPlatforms')}</SelectItem>
                {platforms.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card>
        <CardContent className="p-0">
          {filteredContacts.length === 0 ? (
            <div className="py-12">
              <div className="empty-state">
                <Users className="empty-state-icon" />
                <h3 className="empty-state-title">{t('marketingFull.noContacts')}</h3>
                <p className="empty-state-description">{t('marketingFull.addFirstContact')}</p>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.name')}</TableHead>
                  <TableHead>{t('marketingFull.phoneNumber')}</TableHead>
                  <TableHead>{t('marketingFull.contactEmail')}</TableHead>
                  <TableHead>{t('marketingFull.platform')}</TableHead>
                  <TableHead>{t('marketingFull.dateAdded')}</TableHead>
                  <TableHead>{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.map((contact) => (
                  <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                    <TableCell className="font-medium">{contact.name}</TableCell>
                    <TableCell>
                      {contact.phone && (
                        <span className="flex items-center gap-1 font-numbers">
                          <Phone className="w-3 h-3" />
                          {contact.phone}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {contact.email}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`platform-badge platform-${contact.platform}`}>
                        {getPlatformLabel(contact.platform)}
                      </span>
                    </TableCell>
                    <TableCell className="font-numbers">
                      {new Date(contact.created_at).toLocaleDateString("ar-SA")}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-destructive"
                          onClick={() => handleDelete(contact.id)}
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="text-sm text-muted-foreground text-center">
        {t('marketingFull.totalContacts')} <span className="font-numbers font-bold">{contacts.length}</span>
      </div>
    </div>
  );
}
